import type { IconName } from '@zarya/theme';
import type { AccessMap, MapEdge, MapNode } from './AccessMap.api';
import { t } from '@app/i18n';
import { operatorText } from '@app/main/Policies/PolicyModel';

/**
 * Vocabulário de T19 — legenda, rótulos e subtítulos do inventário do protótipo
 * §2.2 (`AM_TYPE`, `amBuild`).
 */

export type KindVisual = 'human' | 'generalist' | 'specialist' | 'policy' | 'mcp' | 'api' | 'agent';

/**
 * Legenda do mapa (`AM_TYPE`): rótulo, cor do disco e ícone.
 *
 * A cor é o token **do mapa** (`--am-no-*`, em `AccessMap.module.css`), não o
 * token do design system que ele vale no claro. A diferença só aparece no tema
 * escuro: lá o disco precisa de um par (fundo · tinta) que o token sozinho não
 * dá — cinco dos seis fundos invertem com o tema, mas a tinta do ícone e o
 * fundo do `api` não, e era isso que deixava o mapa ilegível no escuro.
 *
 * A tinta não entra aqui porque é uma só para todos os tipos: sai do CSS
 * (`.noIcone`, `.legendaCor`), sem passar pelo TSX.
 */
export const AM_TYPE: Record<KindVisual, { cor: string; icone: IconName }> = {
  // «Pessoa (lente)» e não «Human Subject»: desde a Fase 3 este disco só existe
  // sob a lente «Ver como», e nomeá-lo pelo que ele era prometeria na legenda um
  // nó que o mapa padrão não tem. (O rótulo mora agora em `accessMap.kind.human`.)
  human: { cor: 'var(--am-no-human)', icone: 'person' },
  generalist: { cor: 'var(--am-no-generalist)', icone: 'robot' },
  specialist: { cor: 'var(--am-no-specialist)', icone: 'robot' },
  // `policy` fica no mapa de tipos por compatibilidade — o `kind` continua no
  // enum do contrato —, mas o servidor não emite mais nó de política desde
  // 04/09. Por isso ele NÃO entra em `LEGENDA_KINDS`: anunciar na legenda um
  // disco que o mapa não tem é prometer uma coluna que não existe.
  policy: { cor: 'var(--am-no-policy)', icone: 'file-shield' },
  mcp: { cor: 'var(--am-no-mcp)', icone: 'server' },
  api: { cor: 'var(--am-no-api)', icone: 'link' },
  // Fase 2 (decisão 24): o agente EXTERNO é «um nó de agente igual, com o selo externo» — o mesmo disco dos
  // agentes, e o selo no subtítulo. Fora da legenda pelo motivo de `policy`: só aparece quando o tenant tem um.
  agent: { cor: 'var(--am-no-agent)', icone: 'robot' },
};

/**
 * Rótulo do tipo de nó (legenda e `aria-label` do disco).
 *
 * Função, e não campo da tabela acima: a tabela é const de módulo, avaliada uma
 * vez no import — um `t()` ali congelaria o idioma da primeira renderização.
 */
export function typeLabel(kind: KindVisual): string {
  return t(`accessMap.kind.${kind}`);
}

export const LEGENDA_KINDS: KindVisual[] = ['human', 'generalist', 'specialist', 'mcp', 'api'];

export function inKind(no: MapNode): KindVisual {
  switch (no.kind) {
    case 'human_subject':
      return 'human';
    case 'identidade':
      return no.subtype === 'generalist' ? 'generalist' : 'specialist';
    case 'politica':
      return 'policy';
    default:
      if (no.subtype === 'agent') return 'agent';
      return no.subtype === 'api' ? 'api' : 'mcp';
  }
}

function detalhe(no: MapNode): Record<string, unknown> {
  return (no.detail ?? {}) as Record<string, unknown>;
}

export function detailValue<T>(no: MapNode, chave: string): T | undefined {
  return detalhe(no)[chave] as T | undefined;
}

/**
 * Subtítulo do nó (§2.2): identidade mostra o contrato, resource mostra quantas
 * tools estão sem acesso, o nó humano da lente mostra as claims que o descrevem.
 */
export function inSubtitle(no: MapNode): string {
  if (no.kind === 'identidade') {
    const versao = detailValue<number | null>(no, 'contrato_version');
    return versao == null
      ? t('accessMap.node.noContract')
      : t('accessMap.node.contractVersion', { version: versao });
  }
  if (no.kind === 'resource' && no.subtype === 'agent') {
    return t('accessMap.node.externalAgent');
  }
  if (no.kind === 'resource') {
    const withoutAccess = detailValue<string[]>(no, 'tools_sem_acesso') ?? [];
    return withoutAccess.length > 0
      ? t('accessMap.node.withoutAccess', { n: withoutAccess.length })
      : (no.subtype ?? t('accessMap.kind.resourceFallback'));
  }
  // Nó humano da lente: o subtítulo são as claims que descrevem quem pede — é a
  // resposta a "ver como quem?", e ela não cabe no rótulo, que é o nome do perfil.
  const claims = detailValue<string[]>(no, 'claims') ?? [];
  if (claims.length > 0) return claims.join(' · ');
  return no.subtype ?? t('accessMap.kind.personFallback');
}

/**
 * O que a aresta diz sob a lente: o diagnóstico por eixo, no idioma da tela
 * («eixo workload OK (contrato) · eixo pessoa DENY (a regra não concede X)»).
 *
 * A frase sai dos CAMPOS que o servidor manda, e não do `diagnostic` dele, que é
 * o texto em inglês da API (aim-6ph.23: nada em português sem tradução na tela).
 * A leitura da decisão continua sendo uma só, a do servidor: aqui só se escolhe
 * as palavras, pelos dois catálogos. O operador da condição usa a etiqueta do
 * Detalhe da decisão (T17) e do Simulador (T18) (`operatorText`, aim-zqo).
 */
export function reachText(edge: MapEdge): string {
  const reach = edge.reach;
  if (!reach) return '';
  if (reach.not_evaluated) return notEvaluatedText(reach.not_evaluated);
  if (reach.workload_ok == null || reach.person_ok == null) return '';

  const source = reach.workload_source
    ? reach.workload_source === WORKLOAD_SOURCE_CONTRACT
      ? t('accessMap.reach.source.contract')
      : reach.workload_source
    : null;
  const workload = !reach.workload_ok
    ? t('accessMap.reach.workload.deny')
    : source
      ? t('accessMap.reach.workload.okFrom', { source })
      : t('accessMap.reach.workload.ok');

  const person = !reach.person_ok
    ? reach.person_denial
      ? t('accessMap.reach.person.deny', { reason: denialText(reach.person_denial) })
      : t('accessMap.reach.person.denyBare')
    : reach.person_rule
      ? t('accessMap.reach.person.okBy', { rule: reach.person_rule })
      : t('accessMap.reach.person.ok');

  // « · » é pontuação entre dois dados: compõe-se aqui, e não no catálogo.
  return `${workload} · ${person}`;
}

/** A fonte do eixo workload que passou pelo contrato aprovado (`reach.workload_source`). */
const WORKLOAD_SOURCE_CONTRACT = 'contract';

type PersonDenial = NonNullable<NonNullable<MapEdge['reach']>['person_denial']>;
type NotEvaluated = NonNullable<NonNullable<MapEdge['reach']>['not_evaluated']>;

/** O porquê do DENY do eixo pessoa, pela regra que mais tem a dizer. */
function denialText(denial: PersonDenial): string {
  const rule = denial.rule ?? '';
  switch (denial.kind) {
    case 'condition_failed': {
      const received = conditionValue(denial.received);
      const condition = conditionText(denial);
      return received
        ? t('accessMap.reach.denial.conditionFailedReceived', { rule, condition, received })
        : t('accessMap.reach.denial.conditionFailed', { rule, condition });
    }
    case 'conditions_not_matched':
      return t('accessMap.reach.denial.conditionsNotMatched', { rule });
    case 'rule_does_not_grant':
      return t('accessMap.reach.denial.ruleDoesNotGrant', { rule });
    default:
      return t('accessMap.reach.denial.notGranted', { tool: denial.tool });
  }
}

/**
 * A condição que barrou: a claim, o operador e o valor esperado, cada um só quando o PDP o disse. São dados ligados
 * por espaço, e a ordem é a mesma nos dois idiomas.
 */
function conditionText(denial: PersonDenial): string {
  const parts = [denial.claim ?? ''];
  const operator = operatorText(denial.operator, '');
  if (operator) parts.push(operator);
  const expected = conditionValue(denial.expected);
  if (expected) parts.push(expected);
  return parts.filter(Boolean).join(' ');
}

/**
 * Um valor da condição como a frase o lê — a MESMA conta do `jsonText` do servidor (`usecases/accessmap/lens.go`):
 * ausente e `null` somem (a frase não diz «o token trouxe null»), uma lista de textos sai separada por vírgula e sem
 * colchete, e o resto sai como JSON.
 */
export function conditionValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value.join(', ');
  }
  return JSON.stringify(value);
}

/**
 * Por que o simulador não julgou a aresta. Um código de erro do produto ou do executor vai entre parênteses, como o
 * console mostra os códigos nos avisos de erro. A frase do catálogo de erros NÃO serve aqui: ela fala da ação que
 * falhou («O PDP não aceitou a publicação»), e aqui ninguém publicou nada.
 */
function notEvaluatedText(note: NotEvaluated): string {
  if (note.code === 'simulation_failed') {
    const detail = note.detail?.trim();
    return detail
      ? t('accessMap.reach.simulationFailed', { detail })
      : t('accessMap.reach.simulationFailedNoDetail');
  }
  if (note.code && note.code !== 'not_evaluated') {
    return t('accessMap.reach.notEvaluatedCode', { code: note.code });
  }
  return t('accessMap.reach.notEvaluated');
}

/** Os dois nomes da lente no idioma da tela. */
export interface LensLabels {
  adHocLabel: string;
  grantLabel: string;
}

export function lensLabels(): LensLabels {
  return { adHocLabel: t('accessMap.lens.adHocClaims'), grantLabel: t('accessMap.edge.lensGrant') };
}

/**
 * Os nomes que o servidor escreve para a lente são os da API, em inglês: o do nó humano das claims avulsas e o da
 * aresta do nó humano para a identidade. A tela os troca pelos do catálogo dela, uma vez, sobre o mapa inteiro — o
 * grafo, o Detalhe e a linha do cabeçalho leem o mesmo rótulo (aim-6ph.23).
 *
 * Função pura sobre o `data` da leitura e os dois rótulos (`lensLabels()`): quem a chama a memoiza por eles, senão o
 * grafo recalcula o desenho a cada pintura. Os rótulos mudam com o idioma, e a memória também.
 */
export function withLensLabels(
  data: AccessMap | undefined,
  { adHocLabel, grantLabel }: LensLabels,
): AccessMap | undefined {
  if (!data) return data;
  const adHoc = data.lens.active && data.lens.kind === 'claims';
  return {
    ...data,
    lens: adHoc ? { ...data.lens, label: adHocLabel } : data.lens,
    nodes: data.nodes.map((node) =>
      adHoc && node.kind === 'human_subject' ? { ...node, label: adHocLabel } : node,
    ),
    edges: data.edges.map((edge) =>
      edge.kind === 'human_grant' ? { ...edge, label: grantLabel } : edge,
    ),
  };
}

/** A aresta está fora do alcance da pessoa da lente? (esmaecer, nunca remover) */
export function outOfReach(edge: MapEdge): boolean {
  return edge.reach !== null && edge.reach !== undefined && !edge.reach.reached;
}

/**
 * Limites da aresta como `max_amount ≤ 5000`.
 *
 * Só os que o `rotulo` ainda **não** disse: o contrato descreve `rotulo` como
 * "Tool (nome) com limites" e o exemplo do `getAccessMap` traz
 * `rotulo: "credito.aplicar (max_amount ≤ 50)"` ao lado de
 * `constraints: { max_amount: 50 }`. Escrever os dois lados poria
 * "credito.aplicar (max_amount ≤ 50) · max_amount ≤ 50" na tela.
 */
export function limitsText(edge: MapEdge): string {
  const limits = edge.constraints ?? {};
  const rotulo = edge.label ?? '';
  return Object.entries(limits)
    .filter(([nome]) => !rotulo.includes(nome))
    .map(([nome, valor]) => `${nome} ≤ ${String(valor)}`)
    .join(' · ');
}

/**
 * O que a aresta diz por inteiro. Vai para o `<title>`, que é onde o protótipo
 * põe o detalhe ("passe o mouse para ver os rótulos"): tool e limites.
 *
 * SEM A ORIGEM, desde 04/09. Ela existia para distinguir «contrato da release»
 * de «política extra», e essa distinção morreu com a semântica de união: no eixo
 * identidade a origem é sempre o contrato, e escrevê-la em toda aresta seria
 * repetir a mesma palavra no mapa inteiro. Sob a lente, a aresta que a regra
 * cita não tem origem nenhuma — quem a explica é o diagnóstico por eixo.
 */
export function edgeText(edge: MapEdge): string {
  const parts = [edge.label ?? edge.kind];
  const limits = limitsText(edge);
  if (limits) parts.push(limits);
  // Sob a lente, o diagnóstico por eixo vem junto: é a resposta do hover que o
  // §9 desenha, e o `<title>` é onde o mouse a encontra.
  const reach = reachText(edge);
  if (reach) parts.push(reach);
  return parts.join(' · ');
}

/**
 * O que fica **desenhado** sobre o traço: a tool, sem os limites.
 *
 * Os limites ficam no `<title>` e no painel Detalhe. Um rótulo de 45 caracteres
 * sobre uma curva de 250 px cobre o nó vizinho — o mapa mostra o caminho, o
 * painel é quem se lê como tabela.
 */
export function edgeLabel(edge: MapEdge): string {
  return edge.label ?? edge.kind;
}

export function truncar(texto: string, tamanho: number): string {
  return texto.length > tamanho ? `${texto.slice(0, tamanho - 1)}…` : texto;
}

/**
 * Propósito de T19 (§2.2, reescrito em 04/09).
 *
 * A frase antiga dizia «cada permissão mostra a origem — contrato da release ou
 * política extra», e era a semântica de união escrita na tela: prometia que uma
 * regra de Human Access dá acesso à identidade, que é justamente o que ela não
 * faz. O mapa é o eixo identidade; a pessoa entra pela lente.
 */
export const PROPOSITO = () => t('accessMap.page.tip');

/**
 * Aviso das divergências sob a lente (§9): o catálogo e os contratos não sabem
 * de claims, então as divergências valem para o eixo identidade, com ou sem lente.
 */
export const DIVERGENCES_UNDER_LENS = () => t('accessMap.divergences.underLens');

/** Vazio do painel Detalhe (§5.3). */
export const EMPTY_DETAIL = () => t('accessMap.detail.empty');
